/**
 * Policy Pack Configuration Loader
 * 
 * Loads data source configurations from policy pack for tenant-specific customization.
 * This makes the RFP agent plugin generalizable for other customers.
 */

import { DataSourceConfig } from '../services/dataSourceIntegrations';
import { DataSourceType } from '../services/dataSourceRegistry';

export interface PolicyPackDataSourceConfig {
  enabled: boolean;
  sync_frequency: 'hourly' | 'daily' | 'weekly' | 'monthly';
  filters?: {
    keywords?: string[];
    service_lines?: string[];
    provinces?: string[];
    cities?: string[];
    countries?: string[];
    regions?: string[];
    [key: string]: any;
  };
  api_key?: string;
  api_url?: string;
  credentials?: Record<string, string>;
}

/**
 * Load data source configurations from policy pack
 * 
 * This function can be extended to load from:
 * 1. Policy pack YAML file (via API)
 * 2. Policy pack TypeScript config (for Keystone)
 * 3. Database-stored policy pack metadata
 * 
 * @param policyPackId - Policy pack identifier
 * @param projectId - Project ID for context
 * @returns Record of data source configurations
 */
export async function loadDataSourceConfigsFromPolicyPack(
  policyPackId?: string,
  projectId?: string
): Promise<Record<DataSourceType, DataSourceConfig> | null> {
  // For now, use Keystone-specific config from TypeScript policy pack
  // In the future, this can load from API or database
  
  if (policyPackId === 'keystone-rfp-agent-v1' || policyPackId?.includes('keystone')) {
    try {
      // Import Keystone policy pack (using dynamic import to avoid rootDir restrictions)
      // @ts-ignore - File is outside rootDir but exists at runtime
      const policyPackModule = await import('../../../../../config/policy-packs/keystone-rfp-agent.policy');
      const keystoneRfpAgentPolicy = policyPackModule.keystoneRfpAgentPolicy || policyPackModule.default;
      
      if (keystoneRfpAgentPolicy.data_source_configurations) {
        // Convert policy pack configs to DataSourceConfig format
        const configs: Record<string, DataSourceConfig> = {};
        
        for (const [sourceType, config] of Object.entries(keystoneRfpAgentPolicy.data_source_configurations)) {
          configs[sourceType] = {
            enabled: config.enabled,
            sync_frequency: config.sync_frequency,
            filters: config.filters,
            api_key: config.api_key,
            api_url: config.api_url,
          };
        }
        
        return configs as Record<DataSourceType, DataSourceConfig>;
      }
    } catch (error) {
      console.warn('[PolicyPackConfigLoader] Failed to load Keystone policy pack:', error);
    }
  }
  
  // Fallback: Try to load from API if policy pack ID is provided
  if (policyPackId && projectId) {
    try {
      const { getAPIClient } = await import('@tracekit/core');
      const client = getAPIClient();
      
      // Try to get policy pack from API
      const response = await client.get(`/policy-packs/${policyPackId}`);
      const policyPack = response.data;
      
      if (policyPack?.data_source_configurations) {
        // Parse YAML policy pack data_source_configurations
        const configs: Record<string, DataSourceConfig> = {};
        
        for (const [sourceType, config] of Object.entries(policyPack.data_source_configurations.sources || {})) {
          const sourceConfig = config as any;
          
          // Resolve template variables (e.g., ${keywords}, ${service_lines})
          const resolvedFilters = resolveTemplateVariables(
            sourceConfig.filters || {},
            policyPack.data_source_configurations
          );
          
          configs[sourceType] = {
            enabled: sourceConfig.enabled ?? true,
            sync_frequency: sourceConfig.sync_frequency || 'daily',
            filters: resolvedFilters,
          };
        }
        
        return configs as Record<DataSourceType, DataSourceConfig>;
      }
    } catch (error) {
      console.warn('[PolicyPackConfigLoader] Failed to load policy pack from API:', error);
    }
  }
  
  return null;
}

/**
 * Resolve template variables in filter configuration
 * 
 * Handles variables like:
 * - ${keywords} -> data_source_configurations.keywords
 * - ${project_metadata.service_lines[*].name} -> Extract service line names
 * - ${geographic_preferences.provinces} -> geographic_preferences.provinces
 */
function resolveTemplateVariables(
  filters: Record<string, any>,
  policyPackConfig: any
): Record<string, any> {
  const resolved: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'string' && value.startsWith('${') && value.endsWith('}')) {
      // Template variable - resolve it
      const varPath = value.slice(2, -1);
      resolved[key] = resolveVariablePath(varPath, policyPackConfig);
    } else if (Array.isArray(value)) {
      // Resolve each item in array
      resolved[key] = value.map((item: any) => {
        if (typeof item === 'string' && item.startsWith('${') && item.endsWith('}')) {
          const varPath = item.slice(2, -1);
          return resolveVariablePath(varPath, policyPackConfig);
        }
        return item;
      }).flat();
    } else {
      resolved[key] = value;
    }
  }
  
  return resolved;
}

/**
 * Resolve a variable path like "keywords" or "project_metadata.service_lines[*].name"
 */
function resolveVariablePath(path: string, config: any): any {
  const parts = path.split('.');
  let current: any = config;
  
  for (const part of parts) {
    if (part === '[*]') {
      // Array expansion - skip this part
      continue;
    }
    
    if (part.includes('[*]')) {
      // Array field access like "service_lines[*].name"
      const [arrayKey, ...rest] = part.split('[*]');
      if (current[arrayKey] && Array.isArray(current[arrayKey])) {
        const fieldPath = rest.join('.');
        return current[arrayKey].map((item: any) => {
          if (fieldPath) {
            return getNestedValue(item, fieldPath);
          }
          return item;
        });
      }
    }
    
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return null;
    }
  }
  
  return current;
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && typeof current === 'object' ? current[key] : undefined;
  }, obj);
}

/**
 * Get service lines from policy pack
 */
export async function getServiceLinesFromPolicyPack(
  policyPackId?: string
): Promise<string[]> {
  if (policyPackId === 'keystone-rfp-agent-v1' || policyPackId?.includes('keystone')) {
    try {
      // @ts-ignore - File is outside rootDir but exists at runtime
      const policyPackModule = await import('../../../../../config/policy-packs/keystone-rfp-agent.policy');
      const keystoneRfpAgentPolicy = policyPackModule.keystoneRfpAgentPolicy || policyPackModule.default;
      // Service lines would be in project_metadata, but for now return empty
      // This can be extended to load from policy pack
      return [];
    } catch (error) {
      console.warn('[PolicyPackConfigLoader] Failed to load service lines:', error);
    }
  }
  
  return [];
}

/**
 * Get geographic preferences from policy pack
 */
export async function getGeographicPreferencesFromPolicyPack(
  policyPackId?: string
): Promise<{
  provinces?: string[];
  cities?: string[];
  countries?: string[];
} | null> {
  if (policyPackId === 'keystone-rfp-agent-v1' || policyPackId?.includes('keystone')) {
    try {
      // @ts-ignore - File is outside rootDir but exists at runtime
      const policyPackModule = await import('../../../../../config/policy-packs/keystone-rfp-agent.policy');
      const keystoneRfpAgentPolicy = policyPackModule.keystoneRfpAgentPolicy || policyPackModule.default;
      // Geographic preferences would be in data_source_configurations.geographic_preferences
      // This can be extended to load from policy pack
      return null;
    } catch (error) {
      console.warn('[PolicyPackConfigLoader] Failed to load geographic preferences:', error);
    }
  }
  
  return null;
}

/**
 * Entity Governance Configuration
 */
export interface EntityGovernanceConfig {
  quarantine_rules?: Array<{
    id: string;
    name: string;
    description: string;
    entity_types?: string[];
    source_types?: string[];
    action: 'quarantine' | 'reject';
    threshold?: number;
    keywords?: string[];
  }>;
  source_type_mapping?: {
    internal_document?: string[];
    external_government_opportunity?: string[];
    external_commercial_opportunity?: string[];
    unclassified?: string[];
  };
  enrichment?: {
    basic?: {
      enabled?: boolean;
      functions?: string[];
    };
    deep?: {
      enabled?: boolean;
      schedule?: string;
    };
  };
}

/**
 * Get entity governance configuration from policy pack
 * Includes graceful degradation for policy packs without governance config
 */
export async function getEntityGovernanceFromPolicyPack(
  policyPackId?: string,
  projectId?: string
): Promise<EntityGovernanceConfig | null> {
  // Try to load from Keystone policy pack first
  if (policyPackId === 'keystone-rfp-agent-v1' || policyPackId?.includes('keystone')) {
    try {
      // @ts-ignore - File is outside rootDir but exists at runtime
      const policyPackModule = await import('../../../../../config/policy-packs/keystone-rfp-agent.policy');
      const keystoneRfpAgentPolicy = policyPackModule.keystoneRfpAgentPolicy || policyPackModule.default;
      
      if (keystoneRfpAgentPolicy.entity_governance) {
        return keystoneRfpAgentPolicy.entity_governance;
      }
    } catch (error) {
      console.warn('[PolicyPackConfigLoader] Failed to load entity governance from Keystone policy pack:', error);
    }
  }
  
  // Try to load from API if policy pack ID is provided
  if (policyPackId && projectId) {
    try {
      const { getAPIClient } = await import('@tracekit/core');
      const client = getAPIClient();
      
      const response = await client.get(`/policy-packs/${policyPackId}`);
      const policyPack = response.data;
      
      if (policyPack?.entity_governance) {
        return policyPack.entity_governance;
      }
      
      // Check in policy_data or rules (nested format)
      if (policyPack?.policy_data?.entity_governance) {
        return policyPack.policy_data.entity_governance;
      }
      
      if (policyPack?.rules?.entity_governance) {
        return policyPack.rules.entity_governance;
      }
    } catch (error) {
      console.warn('[PolicyPackConfigLoader] Failed to load entity governance from API:', error);
    }
  }
  
  // Graceful degradation: Return null if not found
  // Calling code should handle null gracefully
  console.info('[PolicyPackConfigLoader] No entity governance configuration found, using default behavior');
  return null;
}

